import { useState } from "react";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { ImageOff, RefreshCw, Upload } from "lucide-react";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { DocumentBlobBase, DocumentBlobKind } from "@bitory-gmbh/bsm";
import imageCompression from "browser-image-compression";
import t from "@/lang/lang";
import {
  PatchPhotoRequest,
  updateHtzReportCache,
  useHtzReportPatchPhotoMutation,
  useHtzReportShowQuery,
} from "@/services/backend/htz/report/api/report-api";
import { patchPhotos } from "@/services/backend/htz/report/types/report";
import { ReportAssessmentRemoveDialog } from "@/services/backend/htz/report/components/report-assessment-remove-dialog";
import { AhuComponentSelect } from "@/services/backend/htz/ahu/components/ahu-component-select";
import {
  StoreRequest,
  useDocumentBlobDeleteMutation,
  useDocumentBlobLoadQuery,
  useDocumentBlobStoreMutation,
} from "@/services/backend/filestorage/api/document-blob-api";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  joinFieldName,
  useHasFieldErrors,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardFooter } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { cn } from "@/shared/lib/utils";

/**
 * ReportPhotoCard edits one photo of the report: its image, the component
 * it documents, a comment and the date it was taken.
 *
 * The image file belongs to the report - copying a photo into a report
 * stores a copy of the file under the report - so this card is what
 * deletes a file once it is no longer referenced.
 */
export function ReportPhotoCard({
  reportId,
  photoId,
}: {
  reportId: UUID;
  photoId: UUID;
}) {
  const { data } = useHtzReportShowQuery({ id: reportId });
  const { resetField } = useErrValidationFailedContext();
  const fieldName = joinFieldName("photos", photoId);

  const request: PatchPhotoRequest = { id: reportId, assessmentId: photoId };
  const [set, { error: setError }] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchPhotoMutation(),
  );

  // Changing the image is a discrete action, not typing, so it calls the
  // mutation directly instead of going through the debounced wrapper.
  const [patch, { isLoading: patchIsLoading, error: patchError }] =
    useHtzReportPatchPhotoMutation();
  const [store, { isLoading: storeIsLoading, error: storeError }] =
    useDocumentBlobStoreMutation();
  const [deleteBlob] = useDocumentBlobDeleteMutation();
  const [compressionError, setCompressionError] = useState("");

  const photo = data?.photos.find((p) => p.id === photoId);

  if (!data || !photo) return;

  const disabled = isImmutable(data);
  const isBusy = patchIsLoading || storeIsLoading;

  // Add or replace: the file is stored under a fresh id and only then
  // referenced, so a failed upload leaves the current image untouched. The
  // replaced file is deleted afterwards, best effort - an orphaned file is
  // invisible to the user, a failed delete must not fail the upload.
  const upload = async (file: File) => {
    setCompressionError("");

    let compressed: File;
    try {
      compressed = await imageCompression(file, {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });
    } catch {
      setCompressionError(t("Das Bild konnte nicht verarbeitet werden."));
      return;
    }

    const previousFileId = photo.fileId;
    const fileId = v4();
    const storeRequest: StoreRequest = {
      ...photoBlob(reportId, fileId),
      mediaType: compressed.type,
      data: new File([compressed], fileId, { type: compressed.type }),
    };

    store(storeRequest)
      .unwrap()
      .then(() =>
        patch({ id: reportId, assessmentId: photoId, fileId })
          .unwrap()
          .catch((err) => {
            // The reference was never taken, so the freshly stored file is
            // orphaned and nothing would ever clean it up. The photo keeps
            // pointing at its current image.
            deleteBlob(photoBlob(reportId, fileId))
              .unwrap()
              .catch(() => {});
            throw err;
          }),
      )
      .then(() => {
        resetField(joinFieldName(fieldName, "fileId"));
        if (previousFileId) {
          deleteBlob(photoBlob(reportId, previousFileId))
            .unwrap()
            .catch(() => {});
        }
      })
      .catch(() => {
        // errors are surfaced via RTKQueryErrorAlert
      });
  };

  // The file is deleted before its reference: a failed patch leaves a
  // missing image the user can upload again, while a dropped reference to
  // an existing file could no longer be cleaned up from the UI.
  const removeImage = () => {
    if (isBusy || !photo.fileId) {
      return;
    }

    deleteBlob(photoBlob(reportId, photo.fileId))
      .unwrap()
      .then(() =>
        patch({ id: reportId, assessmentId: photoId, fileId: null }).unwrap(),
      )
      .catch(() => {
        // errors are surfaced via RTKQueryErrorAlert
      });
  };

  return (
    <Card className="gap-3 py-4">
      <CardContent className="space-y-3 px-4">
        <PhotoImage
          reportId={reportId}
          fileId={photo.fileId}
          fieldName={joinFieldName(fieldName, "fileId")}
        />
        {!disabled && (
          <div className="flex flex-wrap gap-2">
            <UploadLabel
              label={photo.fileId ? t("Bild ersetzen") : t("Bild hochladen")}
              isLoading={isBusy}
              disabled={isBusy}
              onFileSelected={upload}
            />
            {photo.fileId && (
              <Button
                variant="outline"
                size="sm"
                onClick={removeImage}
                disabled={isBusy}
              >
                <ImageOff />
                {t("Bild entfernen")}
              </Button>
            )}
          </div>
        )}
        {compressionError !== "" && (
          <Alert variant="destructive">
            <AlertDescription>{compressionError}</AlertDescription>
          </Alert>
        )}
        <RTKQueryErrorAlert error={storeError || patchError || setError} />
        <AhuComponentSelect
          ahuDataId={data.ahuDataId!}
          componentId={photo.componentId}
          onComponentIdChange={(componentId) =>
            set(
              { ...request, componentId },
              patchPhotos(data, { ...photo, componentId }),
            )
          }
          fieldName={joinFieldName(fieldName, "componentId")}
          disabled={disabled}
        />
        <TextareaField
          label={t("Bemerkung")}
          value={photo.comment}
          onChange={(e) => {
            const comment = e.target.value;
            set(
              { ...request, comment },
              patchPhotos(data, { ...photo, comment }),
            );
          }}
          fieldName={joinFieldName(fieldName, "comment")}
          disabled={disabled}
        />
        <DatePickerField
          label={t("Aufgenommen am")}
          nullable={false}
          date={photo.conductedAt}
          onDateChange={(conductedAt) =>
            set(
              { ...request, conductedAt },
              patchPhotos(data, { ...photo, conductedAt }),
            )
          }
          fieldName={joinFieldName(fieldName, "conductedAt")}
          disabled={disabled}
        />
      </CardContent>
      {!disabled && (
        <CardFooter className="justify-end px-4">
          <ReportAssessmentRemoveDialog
            reportId={reportId}
            assessmentId={photoId}
            title={t("Foto wirklich löschen?")}
            description={t(
              "Das Foto wird mit seinem Bild endgültig aus dem Anlagenbericht gelöscht.",
            )}
            onRemoved={() => {
              // The photo is gone, so its file is deleted afterwards -
              // best effort, an orphaned file is invisible to the user.
              if (photo.fileId) {
                deleteBlob(photoBlob(reportId, photo.fileId))
                  .unwrap()
                  .catch(() => {});
              }
            }}
          />
        </CardFooter>
      )}
    </Card>
  );
}

/**
 * PhotoImage shows the image of a photo. A photo without an image, and one
 * whose file is missing, render a placeholder: a single orphaned reference
 * must not break the card, the image can simply be uploaded again.
 */
function PhotoImage({
  reportId,
  fileId,
  fieldName,
}: {
  reportId: UUID;
  fileId: string | null;
  fieldName: string;
}) {
  // Read-only: uploading an image resets the error.
  const { hasErrors: hasError } = useHasFieldErrors(fieldName);
  const { data, isLoading, error } = useDocumentBlobLoadQuery(
    photoBlob(reportId, fileId ?? ""),
    { skip: !fileId },
  );

  const frame =
    "bg-muted/40 text-muted-foreground flex aspect-4/3 w-full items-center justify-center gap-2 rounded-md border text-sm";

  if (!fileId) {
    return (
      <div className={cn(frame, hasError && "border-red-500")}>
        <ImageOff className="size-4" />
        {t("Kein Bild")}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={frame}>
        <RefreshCw className="size-4 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn(frame, hasError && "border-red-500")}>
        <ImageOff className="size-4" />
        {t("Bild fehlt")}
      </div>
    );
  }

  return (
    <img
      src={data.objectURL}
      alt={t("Foto")}
      className="aspect-4/3 w-full rounded-md object-cover"
    />
  );
}

/**
 * UploadLabel is a file input styled as a button. The input is hidden and
 * wrapped by its label, so the label carries the click and the keyboard
 * handling of the input.
 */
function UploadLabel({
  label,
  isLoading,
  disabled,
  onFileSelected,
}: {
  label: string;
  isLoading?: boolean;
  disabled?: boolean;
  onFileSelected: (file: File) => void;
}) {
  return (
    <Label
      className={cn(
        "border-input bg-background hover:bg-accent hover:text-accent-foreground inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-medium",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <Input
        type="file"
        className="hidden"
        accept="image/jpeg,image/png"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset, so picking the same file again is reported.
          e.target.value = "";
          if (file) {
            onFileSelected(file);
          }
        }}
      />
      {isLoading ? (
        <RefreshCw className="size-4 animate-spin" />
      ) : (
        <Upload className="size-4" />
      )}
      <span>{label}</span>
    </Label>
  );
}

// photoBlob addresses the file of a photo in the file storage. The
// coordinates are the ones the report already uses for its schema image
// and the ones a copied photo is stored under.
function photoBlob(reportId: UUID, fileId: string): DocumentBlobBase {
  return {
    subdomain: "htz",
    documentKind: GSMDocumentKind.Report,
    documentId: reportId,
    version: "",
    kind: DocumentBlobKind.Attachment,
    filename: fileId,
  };
}
